import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Button, Text, useForm, type FormFieldState, type ValidatorFieldState } from '@frontend/design-system';
import { PinField, PIN_MAX_LENGTH, PIN_MIN_LENGTH } from './pin-field';
import { getPinValidationError } from './pin-validation';

export type CreatePinFormProps = {
  onSubmit: (pin: string) => void;
  submitLabel: string;
  isSubmitting?: boolean;
  minLength?: number;
  maxLength?: number;
};

const onlyDigits =
  (maxLength: number) =>
  (value = '') =>
    value.replace(/\D/g, '').slice(0, maxLength);

const pinFieldContainerCss = css`
  > span {
    padding-top: ${theme.spacing(1)};
  }
`;

export function CreatePinForm({
  onSubmit,
  submitLabel,
  isSubmitting = false,
  minLength = PIN_MIN_LENGTH,
  maxLength = PIN_MAX_LENGTH,
}: CreatePinFormProps) {
  const { t } = useTranslation('account');

  const { getFieldProps, formProps, isComplete } = useForm({
    fields: {
      pin: {
        type: 'text',
        required: true,
        validateOnChange: true,
        transformValue: onlyDigits(maxLength),
        validator: (field: ValidatorFieldState<'text'>) => {
          switch (getPinValidationError(field.value, minLength, maxLength)) {
            case 'length':
              return t('Enter a PIN with {{minLength}}-{{maxLength}} digits.', { minLength, maxLength });
            case 'repeated':
              return t('Choose a PIN that is not the same digit repeated.');
            case 'sequential':
              return t('Choose a PIN that is not a sequence of digits.');
            default:
              return '';
          }
        },
      },
      confirmPin: {
        type: 'text',
        required: true,
        validateOnChange: true,
        transformValue: onlyDigits(maxLength),
        validator: (field: ValidatorFieldState<'text'>, allFields: { pin: FormFieldState<'text'> }) => {
          if (field.value !== allFields?.pin?.value) {
            return t('PINs do not match');
          }
          return '';
        },
      },
    },
    onSubmit: (values) => {
      if (!values.pin) {
        return;
      }
      onSubmit(values.pin);
    },
  });

  return (
    <form {...formProps} id='create-pin-form' data-testid='create-pin-form'>
      <PinField
        {...getFieldProps('pin')}
        label={t('{{minLength}}-{{maxLength}} digit PIN', { minLength, maxLength })}
        placeholder={t('Create {{minLength}}-{{maxLength}} digit PIN', { minLength, maxLength })}
        maxLength={maxLength}
        disabled={isSubmitting}
        autoFocus
        containerCss={pinFieldContainerCss}
      />
      <Text
        size='small'
        color='light'
        css={css`
          margin: ${theme.spacing(1)} 0 ${theme.spacing(3)};
        `}
      >
        {t(
          'Choose {{minLength}}-{{maxLength}} digits that are easy for you to remember but hard for others to guess.',
          {
            minLength,
            maxLength,
          }
        )}
      </Text>
      <PinField
        {...getFieldProps('confirmPin')}
        label={t('Confirm PIN')}
        maxLength={maxLength}
        disabled={isSubmitting}
        containerCss={pinFieldContainerCss}
      />
      <Button
        type='submit'
        variant='primary'
        size='large'
        disabled={!isComplete || isSubmitting}
        loading={isSubmitting}
        id='set-pin-button'
        css={css`
          width: 100%;
          margin-top: ${theme.spacing(2)};
        `}
      >
        {submitLabel}
      </Button>
    </form>
  );
}
